import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/feature/AdminLayout";
import { allProducts } from "@/mocks/products";
import type { ProductDetail } from "@/mocks/products";
import ProductFormModal from "./components/ProductFormModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";

export default function AdminProductsPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingProduct, setEditingProduct] = useState<ProductDetail | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState<ProductDetail | null>(null);

  const categories = useMemo(() => {
    const cats = new Map<string, string>();
    allProducts.forEach(p => {
      if (!cats.has(p.categoryId)) {
        const catNames: Record<string, string> = {
          "cat-1": "Electronics",
          "cat-2": "Fashion",
          "cat-3": "Home & Living",
          "cat-4": "Beauty",
          "cat-5": "Sports & Outdoors",
          "cat-6": "Kitchen & Dining",
        };
        cats.set(p.categoryId, catNames[p.categoryId] || p.categoryId);
      }
    });
    return Array.from(cats.entries());
  }, []);

  const filteredProducts = useMemo(() => {
    return allProducts.filter(p => {
      const name = isAr ? p.nameAr : p.nameEn;
      const matchesSearch = searchQuery === "" ||
        name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = categoryFilter === "all" || p.categoryId === categoryFilter;
      const matchesStatus = statusFilter === "all" ||
        (statusFilter === "active" && p.inStock) ||
        (statusFilter === "inactive" && !p.inStock);
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [searchQuery, categoryFilter, statusFilter, isAr]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950">{t("admin.products")}</h1>
            <p className="text-sm text-foreground-500 mt-1">{filteredProducts.length} products found</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Add Product
          </button>
        </div>

        {/* Filters */}
        <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="Search products by name or tag..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-300"
            >
              <option value="all">All Categories</option>
              {categories.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-300"
            >
              <option value="all">All Status</option>
              <option value="active">In Stock</option>
              <option value="inactive">Out of Stock</option>
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70">
                  {["Product", "Category", "Price", "Stock", "Status", "Actions"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                          <img
                            src={product.images[0]}
                            alt={isAr ? product.nameAr : product.nameEn}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-900 truncate max-w-[240px]">
                            {isAr ? product.nameAr : product.nameEn}
                          </p>
                          <p className="text-xs text-foreground-500">{product.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        {categoryFilter === "all" ? (() => {
                          const catNames: Record<string, string> = {
                            "cat-1": "Electronics", "cat-2": "Fashion", "cat-3": "Home & Living",
                            "cat-4": "Beauty", "cat-5": "Sports & Outdoors", "cat-6": "Kitchen & Dining",
                          };
                          return catNames[product.categoryId] || product.categoryId;
                        })() : ""}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">${product.price.toFixed(2)}</span>
                        {product.compareAtPrice && (
                          <span className="text-xs text-foreground-400 line-through whitespace-nowrap">${product.compareAtPrice.toFixed(2)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-700 whitespace-nowrap">{product.stockCount}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap ${
                        product.inStock
                          ? "bg-accent-100 text-accent-800"
                          : "bg-foreground-100 text-foreground-600"
                      }`}>
                        {product.inStock ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingProduct(product)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeletingProduct(product)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredProducts.length === 0 && (
            <div className="py-16 text-center">
              <i className="ri-inbox-line text-3xl text-foreground-300 mb-3 block"></i>
              <p className="text-sm text-foreground-500">No products match your filters</p>
            </div>
          )}
        </div>

        {/* Modals */}
        {showAddModal && (
          <ProductFormModal
            onClose={() => setShowAddModal(false)}
            onSave={() => setShowAddModal(false)}
          />
        )}
        {editingProduct && (
          <ProductFormModal
            product={editingProduct}
            onClose={() => setEditingProduct(null)}
            onSave={() => setEditingProduct(null)}
          />
        )}
        {deletingProduct && (
          <DeleteConfirmModal
            productName={isAr ? deletingProduct.nameAr : deletingProduct.nameEn}
            onCancel={() => setDeletingProduct(null)}
            onConfirm={() => setDeletingProduct(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}